import React, {useState, useEffect, useRef, useMemo, useCallback} from "react";
import edit from "../../assets/ellipsis.svg";
import {deleteDish, updateDishStatus} from "../../services/menu-service.js";
import {StatusTranslationMap} from "../../constants/index.js";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import ConfirmationModal from "../../components/Modal/ConfirmationModal.jsx";
import { DISH_IMAGE_PLACEHOLDER_WIDE } from "../../utils/dishImagePlaceholder.js";

// Status badge helper functions - moved outside component for performance
const getStatusBadgeBackground = (status) => {
    const colorMap = {
        PENDING: '#FEF3C7', // Light amber
        ACTIVE: '#D1FAE5', // Light green
        DISABLED: '#F3F4F6', // Light gray
        REJECTED: '#FEE2E2', // Light red
    };
    return colorMap[status] || '#F3F4F6';
};

const getStatusBadgeTextColor = (status) => {
    const colorMap = {
        PENDING: '#92400E', // Dark amber
        ACTIVE: '#065F46', // Dark green
        DISABLED: '#6B7280', // Medium gray
        REJECTED: '#991B1B', // Dark red
    };
    return colorMap[status] || '#6B7280';
};

const getStatusBadgeBorderColor = (status) => {
    const colorMap = {
        PENDING: '#FCD34D', // Amber
        ACTIVE: '#34D399', // Green
        DISABLED: '#D1D5DB', // Gray
        REJECTED: '#F87171', // Red
    };
    return colorMap[status] || '#D1D5DB';
};

// Removed preloadImage function - browser native lazy loading is more efficient
// and doesn't block the main thread with synchronous DOM operations

const MenuItem = React.memo(({item, viewMode = 'grid', fetchMenus, onDishClick, toastRef, isSelectionMode = false, isSelected = false, onSelectChange}) => {
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [isConfirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
    const [isConfirmDisableVisible, setConfirmDisableVisible] = useState(false);
    const { language } = useLanguage();
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    // Handle clicks outside of the kebab menu - must be before any conditional returns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isEditing && 
                menuRef.current && 
                !menuRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)) {
                setIsEditing(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isEditing]);

    // Removed Intersection Observer - browser native lazy loading is sufficient
    // Creating multiple observers was causing performance issues

    const disableDish = useCallback(async (id) => {
        try {
            await updateDishStatus(id, "DISABLED");
            if (toastRef.current) {
                toastRef.current.addToast(
                    getTranslation('toast.statusUpdated', language) || "Status updated successfully",
                    "success"
                );
            }
            fetchMenus();
        } catch (error) {
            if (toastRef.current) {
                toastRef.current.addToast(
                    getTranslation('toast.updateFailed', language) || "Status not updated",
                    "error"
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, fetchMenus]);

    const enableDish = useCallback(async (id) => {
        try {
            await updateDishStatus(id, "ACTIVE");
            if (toastRef.current) {
                toastRef.current.addToast(
                    getTranslation('toast.statusUpdated', language) || "Status updated successfully",
                    "success"
                );
            }
            fetchMenus();
        } catch (error) {
            if (toastRef.current) {
                toastRef.current.addToast(
                    getTranslation('toast.updateFailed', language) || "Status not updated",
                    "error"
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, fetchMenus]);

    const deleteDishFromKms = useCallback(async (id) => {
        try {
            await deleteDish(id);
            toastRef.current?.addToast(getTranslation('menu.dishDisabledSuccess', language) || "Կերակուրը հաջողությամբ անջատված է", "success");
            fetchMenus();
        } catch (error) {
            toastRef.current?.addToast(getTranslation('menu.dishDisabledError', language) || "Չհաջողվեց անջատել կերակուրը", "error");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, fetchMenus]);

    const handleCardClick = useCallback((e) => {
        if (!item) return;
        
        // Don't navigate if clicking on the edit menu or checkbox
        if (e.target.closest('.edit-menu-container') || e.target.closest('.selection-checkbox-container')) {
            return;
        }
        
        // In selection mode, toggle selection instead of navigating
        if (isSelectionMode) {
            e.stopPropagation();
            if (onSelectChange) {
                onSelectChange(item.id, !isSelected);
            }
            return;
        }
        
        // If onDishClick callback is provided, use it (for modal)
        if (onDishClick) {
            onDishClick(item);
        } else {
            // Otherwise, navigate to dish detail page (fallback)
            navigate(`/menu/dish/${item.id}`);
        }
    }, [onDishClick, item, navigate, isSelectionMode, isSelected, onSelectChange]);


    const dishName = useMemo(() => {
        if (!item) return "";
        const langKey = `name${language.charAt(0).toUpperCase() + language.slice(1)}`;
        return item[langKey] || item.nameHy || "";
    }, [item, language]);

    // Memoize image style object to prevent recreation on every render
    const imageStyle = useMemo(() => ({
        opacity: 0
    }), []);

    // Memoize status badge style to prevent recreation on every render
    // Must be before early return to follow React Hooks rules
    const statusBadgeStyle = useMemo(() => {
        if (!item) {
            return {
                backgroundColor: '#F3F4F6',
                color: '#6B7280',
                border: '1.5px solid #D1D5DB'
            };
        }
        return {
            backgroundColor: getStatusBadgeBackground(item.status),
            color: getStatusBadgeTextColor(item.status),
            border: `1.5px solid ${getStatusBadgeBorderColor(item.status)}`
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item?.status]);

    // Early return after all hooks
    if (!item) {
        return null;
    }

    const isPending = item.status === "PENDING";

    return (
        <div className={viewMode === "grid" ? "h-full w-full min-w-0" : "w-full"}>
            {viewMode === 'list' ? (
                <div
                    onClick={handleCardClick}
                    className={`relative bg-white dark:bg-[#161b22] border rounded-xl shadow-sm cursor-pointer w-full flex flex-row items-center p-3 gap-4 hover:shadow-md transition-shadow ${
                        isEditing ? "z-[200]" : ""
                    } ${
                        isSelectionMode && isSelected 
                            ? 'border-gray-400 dark:border-gray-500 border-2 bg-gray-100 dark:bg-[#1c2128]' 
                            : 'border-gray-200 dark:border-[#30363d]'
                    }`}
                    style={
                        isEditing
                            ? undefined
                            : { transform: "translateZ(0)", backfaceVisibility: "hidden" }
                    }
                    role="button"
                    aria-label={`Dish: ${dishName}`}
                >
                    {/* Selection Checkbox */}
                    {isSelectionMode && (
                        <div 
                            className="z-30 selection-checkbox-container w-6 h-6 flex-shrink-0 bg-white dark:bg-[#0d1117] rounded flex items-center justify-center shadow-sm"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    if (onSelectChange) {
                                        onSelectChange(item.id, e.target.checked);
                                    }
                                }}
                                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 bg-white dark:bg-[#0d1117] focus:ring-0 focus:ring-offset-0 focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ accentColor: '#18181B' }}
                            />
                        </div>
                    )}

                    {/* Image */}
                    <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100 dark:bg-[#0d1117]">
                        <img
                            className="w-full h-full object-cover"
                            src={item.url}
                            alt={dishName || "Food Item"}
                            onLoad={(e) => { e.target.style.opacity = "1"; }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = DISH_IMAGE_PLACEHOLDER_WIDE;
                                e.target.style.opacity = "1";
                            }}
                            loading="lazy"
                            decoding="async"
                            style={imageStyle}
                        />
                    </div>

                    {/* Info */}
                    <div className="flex-1 flex flex-col justify-center min-w-0 py-1">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2">
                            <div className="flex-1 min-w-0 pr-2">
                                <h3 className="text-base font-bold text-gray-900 dark:text-[#c9d1d9] leading-snug line-clamp-1 sm:line-clamp-2">
                                    {dishName}
                                </h3>
                                {isPending && (
                                    <div className="mt-2 text-xs text-amber-800 dark:text-amber-300 flex items-center bg-amber-50 dark:bg-amber-950/40 px-2 py-1.5 rounded-md border border-amber-200 dark:border-amber-800/50 w-fit">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        <span className="font-semibold">{getTranslation('menu.pendingApproval', language) || "Սպասում է հաստատման"}</span>
                                    </div>
                                )}
                                <div className="font-bold text-sm sm:text-base text-gray-900 dark:text-[#c9d1d9] mt-1 sm:mt-2">
                                    {item.price === 0 ? getTranslation('menu.configurable', language) : `${item.price} AMD`}
                                </div>
                            </div>
                            {/* Status Badge */}
                            <div className="flex-shrink-0 mt-1 sm:mt-0">
                                <span className="inline-block text-[10px] sm:text-xs font-bold px-2 py-1 sm:px-3 sm:py-1 rounded-full shadow-sm" style={statusBadgeStyle}>
                                    {StatusTranslationMap[item.status] || item.status}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Actions Button */}
                    {!isPending && !isSelectionMode && (
                        <div className="relative ml-2 flex-shrink-0 flex items-center">
                            <button
                                ref={buttonRef}
                                className="flex items-center justify-center h-9 w-9 bg-white dark:bg-[#0d1117] hover:bg-gray-50 dark:hover:bg-[#161b22] active:bg-gray-100 dark:active:bg-[#1c2128] rounded-lg transition-all duration-150 edit-menu-container border border-gray-200 dark:border-[#30363d]"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsEditing((prev) => !prev);
                                }}
                                aria-label="Menu options"
                            >
                                <img
                                    src={edit || ""}
                                    alt="edit"
                                    className="w-4 h-4 dark:invert dark:opacity-80"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </button>

                            {/* Edit menu dropdown */}
                            {isEditing && (
                                <div 
                                    ref={menuRef}
                                    className="absolute top-full right-0 mt-2 w-36 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl shadow-xl z-[210] edit-menu-container overflow-hidden"
                                >
                                    <div className="py-1">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsEditing(false);
                                                navigate(`/menu/edit/${item.id}`);
                                            }}
                                            className="w-full px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#1c2128] flex items-center text-left transition-colors text-gray-900 dark:text-[#c9d1d9]"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3 text-gray-600 dark:text-[#8b949e]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                            <span className="text-sm font-medium">{getTranslation('common.edit', language)}</span>
                                        </button>
                                        {item.status === "DISABLED" ? (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsEditing(false);
                                                    enableDish(item.id);
                                                }}
                                                className="w-full px-4 py-2.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/30 flex items-center text-left transition-colors"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span className="text-sm font-medium">{getTranslation('menu.enableDish', language)}</span>
                                            </button>
                                        ) : (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsEditing(false);
                                                    setConfirmDisableVisible(true);
                                                }}
                                                className="w-full px-4 py-2.5 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950/30 flex items-center text-left transition-colors"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                                                </svg>
                                                <span className="text-sm font-medium">{getTranslation('menu.disableDish', language)}</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsEditing(false);
                                                setConfirmDeleteVisible(true);
                                            }}
                                            className="w-full px-4 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center text-left transition-colors"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            <span className="text-sm font-medium">{getTranslation('common.delete', language)}</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
            <div
                onClick={handleCardClick}
                className={`relative bg-white dark:bg-[#161b22] border rounded-xl shadow-sm cursor-pointer w-full h-full flex flex-col ${
                    isEditing ? "z-[200] overflow-visible" : "overflow-hidden"
                } ${
                    isSelectionMode && isSelected 
                        ? 'border-gray-400 dark:border-gray-500 border-2 bg-gray-100 dark:bg-[#1c2128]' 
                        : 'border-gray-200 dark:border-[#30363d]'
                }`}
                style={
                    isEditing
                        ? undefined
                        : {
                            transform: "translateZ(0)",
                            backfaceVisibility: "hidden"
                        }
                }
                role="button"
                aria-label={`Dish: ${dishName}`}
            >
                {/* Selection Checkbox - appears in top-left when selection mode is active */}
                {isSelectionMode && (
                    <div 
                        className="absolute top-3 left-3 z-30 selection-checkbox-container w-6 h-6 bg-white dark:bg-[#0d1117] rounded flex items-center justify-center shadow-sm"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                                e.stopPropagation();
                                if (onSelectChange) {
                                    onSelectChange(item.id, e.target.checked);
                                }
                            }}
                            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 bg-white dark:bg-[#0d1117] focus:ring-0 focus:ring-offset-0 focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                                accentColor: '#18181B'
                            }}
                        />
                    </div>
                )}

                {/* Status badge - corner indicator */}
                <div className="absolute top-3 right-3 z-20">
                    <span
                        className="inline-block text-[10px] font-bold px-3 py-1.5 rounded-full shadow-md"
                        style={statusBadgeStyle}
                    >
                        {StatusTranslationMap[item.status] || item.status}
                    </span>
                </div>

                {/* Image container with optimized rendering */}
                <div className="w-full h-[160px] flex-shrink-0 overflow-hidden bg-white dark:bg-[#0d1117]">
                    <img
                        className="w-full h-full object-cover"
                        src={item.url}
                        alt={dishName || "Food Item"}
                        onLoad={(e) => {
                            e.target.style.opacity = "1";
                        }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DISH_IMAGE_PLACEHOLDER_WIDE;
                            e.target.style.opacity = "1";
                        }}
                        loading="lazy"
                        decoding="async"
                        style={imageStyle}
                    />
                </div>

                {/* Content area */}
                <div className="p-4 flex flex-col gap-3 flex-1">
                    {/* Dish name - improved typography */}
                    <h3 className="text-base font-bold text-gray-900 dark:text-[#c9d1d9] leading-snug line-clamp-2 min-h-[48px]">
                        {dishName}
                    </h3>

                    {/* Pending approval message */}
                    {isPending && (
                        <div className="text-xs text-amber-800 dark:text-amber-300 flex items-center bg-amber-50 dark:bg-amber-950/40 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-800/50">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span className="font-semibold">{getTranslation('menu.pendingApproval', language) || "Սպասում է հաստատման"}</span>
                        </div>
                    )}

                    {/* Bottom section with price and actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-[#30363d] mt-auto">
                        {/* Price */}
                        <div className="font-bold text-base text-gray-900 dark:text-[#c9d1d9]">
                            {item.price === 0 ? (
                                <span>{getTranslation('menu.configurable', language)}</span>
                            ) : (
                                `${item.price} AMD`
                            )}
                        </div>

                        {/* Actions button - Hide in selection mode */}
                        {!isPending && !isSelectionMode && (
                            <div className="relative">
                                <button
                                    ref={buttonRef}
                                    className="flex items-center justify-center h-9 w-9 bg-white dark:bg-[#0d1117] hover:bg-gray-50 dark:hover:bg-[#161b22] active:bg-gray-100 dark:active:bg-[#1c2128] rounded-lg transition-all duration-150 edit-menu-container border border-gray-200 dark:border-[#30363d]"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsEditing((prev) => !prev);
                                    }}
                                    aria-label="Menu options"
                                >
                                    <img 
                                        src={edit || ""} 
                                        alt="edit" 
                                        className="w-4 h-4 dark:invert dark:opacity-80"
                                        onError={(e) => {
                                            console.error("Failed to load edit icon:", edit);
                                            e.target.onerror = null;
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </button>

                                {/* Edit menu dropdown */}
                                {isEditing && (
                                    <div 
                                        ref={menuRef}
                                        className="absolute bottom-full right-0 mb-2 w-36 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl shadow-xl z-[210] edit-menu-container overflow-hidden"
                                    >
                                        <div className="py-1">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsEditing(false);
                                                    navigate(`/menu/edit/${item.id}`);
                                                }}
                                                className="w-full px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#1c2128] flex items-center text-left transition-colors text-gray-900 dark:text-[#c9d1d9]"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3 text-gray-600 dark:text-[#8b949e]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                                <span className="text-sm font-medium">{getTranslation('common.edit', language)}</span>
                                            </button>
                                            {item.status === "DISABLED" ? (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsEditing(false);
                                                        enableDish(item.id);
                                                    }}
                                                    className="w-full px-4 py-2.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/30 flex items-center text-left transition-colors"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <span className="text-sm font-medium">{getTranslation('menu.enableDish', language)}</span>
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsEditing(false);
                                                        setConfirmDisableVisible(true);
                                                    }}
                                                    className="w-full px-4 py-2.5 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950/30 flex items-center text-left transition-colors"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                                                    </svg>
                                                    <span className="text-sm font-medium">{getTranslation('menu.disableDish', language)}</span>
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsEditing(false);
                                                    setConfirmDeleteVisible(true);
                                                }}
                                                className="w-full px-4 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center text-left transition-colors"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                <span className="text-sm font-medium">{getTranslation('common.delete', language)}</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            )}

            {isConfirmDeleteVisible && (
                <ConfirmationModal
                    description={getTranslation('menu.confirmDelete', language) || "Վստա՞հ եք, որ ցանկանում եք ջնջել այս կերակուրը:"}
                    onCancel={() => setConfirmDeleteVisible(false)}
                    onSubmit={() => {
                        deleteDishFromKms(item.id).then(() => setConfirmDeleteVisible(false));
                    }}
                />
            )}
            {isConfirmDisableVisible && (
                <ConfirmationModal
                    description={getTranslation('menu.confirmDisable', language) || "Վստա՞հ եք, որ ցանկանում եք անջատել այս կերակուրը:"}
                    onCancel={() => setConfirmDisableVisible(false)}
                    onSubmit={() => {
                        disableDish(item.id).then(() => setConfirmDisableVisible(false));
                    }}
                />
            )}
        </div>
    );
});

MenuItem.displayName = 'MenuItem';

export default MenuItem;
